import React, { useState, useEffect, useContext, useRef, useMemo } from 'react'
import { useMutation, useQuery } from "@apollo/client"
import "react-awesome-lightbox/build/style.css"
import "../Material/ListingHeader.css"
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore"
import { firestoreDb } from "../Components/firebase"
import ReactMarkdown from 'react-markdown'
import moment from 'moment-timezone'
import withApolloClient from '../withApolloClient'
import { CurrentUserContext } from '../Context/CurrentUserContext'
import { 
    ASSIGN_CONVERSATION, 
    SUBSCRIBE_CONVERSATION, 
    UNSUBSCRIBE_CONVERSATION,
    UPDATE_CONVERSATION,
} from './Mutations'
import RepAvatar from '../Material/RepAvatar'
import MessageStatus from './MessageStatus'
import './MessageStatus.css'
import { GET_CONVERSATION, GET_INBOX_MEMBERS, ASSIGNED_UNREAD_COUNT_QUERY } from './Queries'
import ExternalIntegrations from '../Drivers/Driver/Detail/DetailsNew/Components/ExternalIntegrations'
import { debounce } from 'lodash'
import { Link } from 'react-router-dom'
import Loader from '../Material/Loader'
import MessageComposer from './SendMessage/MessageComposer'
import { UserQuery } from '../Functions/querys'

const COMMS_MESSAGES_COLLECTION_NAME = "messaging_message"
const NOTES_COLLECTION_NAME = "messaging_conversationnote"

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'heic', 'heif', 'avif'])

const extractFilename = (urlOrName) => {
    if (!urlOrName) return 'Attachment'
    const source = urlOrName.split('?')[0]
    const parts = source.split('/')
    const lastPart = parts[parts.length - 1] || urlOrName
    try {
        return decodeURIComponent(lastPart)
    } catch (error) {
        return lastPart
    }
}

const extensionFromName = (name) => {
    if (!name) return ''
    const parts = name.split('.')
    if (parts.length < 2) return ''
    return parts.pop().toLowerCase()
}

const isImageAttachment = ({ mimeType, name, url }) => {
    if (mimeType && mimeType.startsWith('image/')) return true
    const extension = extensionFromName(name || extractFilename(url))
    return IMAGE_EXTENSIONS.has(extension)
}

const formatBytes = (bytes) => {
    if (!bytes || Number.isNaN(bytes)) return null
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const getAttachmentGlyph = (mimeTypeOrName) => {
    const value = mimeTypeOrName?.toLowerCase() || ''
    if (value.includes('pdf') || value.endsWith('.pdf')) return '📄'
    if (value.includes('sheet') || value.endsWith('.xls') || value.endsWith('.xlsx') || value.endsWith('.csv')) return '📊'
    if (value.includes('word') || value.endsWith('.doc') || value.endsWith('.docx')) return '📝'
    if (value.includes('video') || value.endsWith('.mp4') || value.endsWith('.mov')) return '🎥'
    if (value.includes('audio') || value.endsWith('.mp3') || value.endsWith('.wav')) return '🎧'
    return '📎'
}

const normalizeMessageAttachments = (msg) => {
    if (!msg) return []
    const messagePayload = typeof msg.message === 'object' && !Array.isArray(msg.message) ? (msg.message || {}) : {}
    const payloadUrls = Array.isArray(messagePayload.attachment_urls) ? messagePayload.attachment_urls.filter(Boolean) : []
    const rootUrls = Array.isArray(msg.attachment_urls) ? msg.attachment_urls.filter(Boolean) : []
    const combinedUrls = [...payloadUrls, ...rootUrls]

    const attachmentsFromUrls = combinedUrls.map((url, index) => {
        const name = extractFilename(url)
        return {
            id: `url-${index}`,
            url,
            name,
            size: null,
            mimeType: null,
            isImage: isImageAttachment({ url, name }),
        }
    })

    const legacyAttachments = Array.isArray(msg.attachments) ? msg.attachments.filter(Boolean).map((attachment, index) => {
        const url = attachment.file_url || attachment.url
        const name = attachment.file_name || extractFilename(url)
        const mimeType = attachment.file_type || attachment.mime_type || ''
        return {
            id: `legacy-${index}`,
            url,
            name,
            size: attachment.file_size || null,
            mimeType,
            isImage: isImageAttachment({ mimeType, name, url }),
        }
    }) : []

    const mapped = [...legacyAttachments, ...attachmentsFromUrls].filter((attachment) => attachment.url)
    const seen = new Set()
    return mapped.filter((attachment) => {
        if (seen.has(attachment.url)) return false
        seen.add(attachment.url)
        return true
    })
}

const formatUserDisplayName = (user) => {
    if (!user) return ''
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
    return fullName || user.username || 'Unassigned'
}



const CommsConversation = ({ conversationIdFromParent, createResponseForPrompt, slimView = false, ...props }) => {
    const { currentUser } = useContext(CurrentUserContext)
    const [messages, setMessages] = useState([])
    const [notes, setNotes] = useState([])
    const [conversationId, setConversationId] = useState(conversationIdFromParent)
    const [actionMenuOpen, setActionMenuOpen] = useState(false)
    const [selectedAssigneeId, setSelectedAssigneeId] = useState('')
    const [assignError, setAssignError] = useState(null)
    const [selectedSubscriberId, setSelectedSubscriberId] = useState('')
    const [subscriptionError, setSubscriptionError] = useState(null)
    const [subscribersMenuOpen, setSubscribersMenuOpen] = useState(false)
    const messagesContainerRef = useRef(null)
    const subscribersMenuRef = useRef(null)
    const subscribersButtonRef = useRef(null)
    const prevTotalMessagesCountRef = useRef(0)

    const [updateConversationMutation] = useMutation(UPDATE_CONVERSATION, {
        refetchQueries: [{ query: GET_CONVERSATION, variables: { id: conversationId } }, { query: ASSIGNED_UNREAD_COUNT_QUERY}],
        onError: (error) => console.error('Update conversation error', error),
    })

    const {
        data: conversationData,
        loading: conversationLoading,
        error: conversationError,
        refetch: refetchConversation,
    } = useQuery(GET_CONVERSATION, {
        variables: { id: conversationId },
        skip: !conversationId,
        fetchPolicy: 'cache-and-network',
    })

    const conversation = conversationData?.conversation || null
    const inboxId = conversation?.channel?.inbox?.id
    const {
        data: inboxMembersData,
        loading: inboxMembersLoading,
    } = useQuery(GET_INBOX_MEMBERS, {
        variables: { inboxId, first: 100 },
        skip: !inboxId,
        fetchPolicy: 'cache-first',
    })

    const conversationMembers = useMemo(() => {
        if (!inboxMembersData?.allInboxMembers?.edges) return []
        return inboxMembersData.allInboxMembers.edges
            .map(edge => edge?.node)
            .filter(node => node?.user)
    }, [inboxMembersData])

    const assignableMembers = useMemo(() => (
        conversationMembers.filter(member => member?.canBeAssigned)
    ), [conversationMembers])

    const assignedUserId = useMemo(() => (
        conversation?.assignedTo?.id ? conversation.assignedTo.id : null
    ), [conversation?.assignedTo?.id])

    const isCurrentUserAssigned = useMemo(() => (
        conversation?.assignedTo && currentUser && conversation?.assignedTo?.username === currentUser?.username
        // Use username comparison to avoid type coercion issues (ID might be different even if same type and user)
    ), [conversation?.assignedTo?.id])

    const currentUserIdString = useMemo(() => (
        currentUser?.id ? String(currentUser.id) : null
    ), [currentUser?.id])

    const debouncedRefetch = useMemo(
        () => debounce(() => refetchConversation(), 250),
        [refetchConversation]
    )
    useEffect(() => {
        // When the component unmounts, cancel debounce, otherwise React will warn you about state updates on unmounted components.
        return () => {debouncedRefetch.cancel()}
    }, [debouncedRefetch]) 

    const baseConversationId = conversation?.id

    const [assignConversationMutation, { loading: assignLoading }] = useMutation(ASSIGN_CONVERSATION, {
        refetchQueries: [{ query: ASSIGNED_UNREAD_COUNT_QUERY }],
        onCompleted: (data) => {
            const result = data?.messagingAssignConversation
            if (result?.ok) {
                debouncedRefetch()
                setAssignError(null)
            } else if (result?.errors && result.errors.length > 0) {
                setAssignError(result.errors.join(', '))
            } else {
                setAssignError('Unable to assign conversation')
            }
        },
        onError: (error) => {
            console.error('Assign conversation error', error)
            setAssignError(error.message || 'Error assigning conversation')
        },
    })

    const [subscribeConversationMutation, { loading: subscribeLoading }] = useMutation(SUBSCRIBE_CONVERSATION, {
        onCompleted: (data) => {
            const result = data?.messagingSubscribeConversation
            if (result?.ok) {
                debouncedRefetch()
                setSubscriptionError(null)
            } else if (result?.errors && result.errors.length > 0) {
                setSubscriptionError(result.errors.join(', '))
            } else {
                setSubscriptionError('Unable to subscribe to conversation')
            }
        },
        onError: (error) => {
            console.error('Subscribe conversation error', error)
            setSubscriptionError(error.message || 'Error subscribing to conversation')
        },
    })

    const [unsubscribeConversationMutation, { loading: unsubscribeLoading }] = useMutation(UNSUBSCRIBE_CONVERSATION, {
        onCompleted: (data) => {
            const result = data?.messagingUnsubscribeConversation
            if (result?.ok) {
                debouncedRefetch()
                setSubscriptionError(null)
            } else if (result?.errors && result.errors.length > 0) {
                setSubscriptionError(result.errors.join(', '))
            } else {
                setSubscriptionError('Unable to unsubscribe from conversation')
            }
        },
        onError: (error) => {
            console.error('Unsubscribe conversation error', error)
            setSubscriptionError(error.message || 'Error unsubscribing from conversation')
        },
    })

    const getMessages = async () => {
        if (!conversationId) {
            setMessages([])
            return
        }
        const q = query(
            collection(firestoreDb, COMMS_MESSAGES_COLLECTION_NAME),
            where("conversation_graphene_id", "==", conversationId),
            limit(100),
            orderBy("timestamp", "desc")
        )
        onSnapshot(q, (snapshot) => {
            const docs = snapshot.docs
            const messages = docs.map(doc => ({ id: doc.id, ...doc.data(), type: "message" }))
            setMessages(messages)
            if (docs.length > prevTotalMessagesCountRef.current) {
                debouncedRefetch()
            }
            prevTotalMessagesCountRef.current = docs.length
        })
    }

    const getNotes = async () => {
        if (!conversationId) {
            setNotes([])
            return
        }
        const q = query(
            collection(firestoreDb, NOTES_COLLECTION_NAME),
            where("conversation_graphene_id", "==", conversationId),
            limit(50),
            orderBy("date_added", "desc")
        )
        onSnapshot(q, (snapshot) => {
            const fetchedNotes = []
            snapshot.forEach((doc) => {
                fetchedNotes.push({ id: doc.id, ...doc.data(), type: "note" })
            })
            setNotes(fetchedNotes)
        })
    }

    useEffect(() => {
        setConversationId(conversationIdFromParent)
    }, [conversationIdFromParent])

    useEffect(() => {
        getMessages()
        getNotes()
    }, [conversationId])

    const combinedFeed = useMemo(() => {
        return [...messages, ...notes].sort(
            (a, b) => new Date(a.timestamp || a.date_added) - new Date(b.timestamp || b.date_added)
        )
    }, [messages, notes])

    const lastInboundSuggestedResponse = useMemo(() => {
        // Check if last message is inbound and has suggestion
        if (messages.length === 0) return ''
        const lastMessage = messages[0]

        if (!lastMessage?.is_inbound) return ''
        const suggestion = lastMessage?.ai_agent_suggested_response || lastMessage?.message?.ai_agent_suggested_response
        if (suggestion) {
            return suggestion
        }
        return ''
    }, [messages])

    useEffect(() => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
        }
    }, [combinedFeed])

    useEffect(() => {
        if (conversation?.assignedTo?.id) {
            setSelectedAssigneeId(conversation.assignedTo.id)
        } else if (!assignLoading) {
            setSelectedAssigneeId('')
        }
    }, [conversation?.assignedTo?.id, assignLoading])

    const handleAssigneeChange = async (event) => {
        const value = event.target.value
        setSelectedAssigneeId(value)
        setAssignError(null)

        if (!value || !baseConversationId) {
            return
        }

        try {
            await assignConversationMutation({
                variables: {
                    conversationId: baseConversationId,
                    userId: value,
                },
            })
        } catch (error) {
            // Errors handled via onError callback
        }
    }

    useEffect(() => {
        if (!baseConversationId || !currentUserIdString || conversation?.isReadByAssignedAgent) return
        updateConversationMutation({ 
            variables: {conversationId: baseConversationId,isReadByAssignedAgent: true},
            refetchQueries: [UserQuery],
        })
    }, [baseConversationId, currentUserIdString, updateConversationMutation])

    const subscriberEdges = conversation?.subscribers?.edges || []
    const subscribers = useMemo(() => (
        subscriberEdges
            .map(edge => edge?.node)
    ), [subscriberEdges])

    const subscriberIdSet = useMemo(() => new Set(subscribers.map(subscriber => subscriber.id)), [subscribers])

    const availableSubscriberOptions = useMemo(() => {
        if (!conversationMembers || conversationMembers.length === 0) return []
        const seen = new Set()
        const options = []
        conversationMembers.forEach(member => {
            if (subscriberIdSet.has(member.user.id) || seen.has(member.user.id)) {
                return
            }
            seen.add(member.user.id)
            options.push({
                value: member.user.id,
                label: formatUserDisplayName(member.user),
            })
        })
        return options
    }, [conversationMembers, subscriberIdSet])

    useEffect(() => {
        if (selectedSubscriberId && !availableSubscriberOptions.some(option => option.value === selectedSubscriberId)) {
            setSelectedSubscriberId('')
        }
    }, [selectedSubscriberId, availableSubscriberOptions])

    useEffect(() => {
        setSelectedSubscriberId('')
        setSubscriptionError(null)
    }, [conversation?.id])

    useEffect(() => {
        setSubscribersMenuOpen(false)
        setActionMenuOpen(false)
    }, [conversation?.id])

    useEffect(() => {
        if (!subscribersMenuOpen) return

        const handleClickOutside = (event) => {
            if (
                subscribersMenuRef.current &&
                !subscribersMenuRef.current.contains(event.target) &&
                !subscribersButtonRef.current?.contains(event.target)
            ) {
                setSubscribersMenuOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [subscribersMenuOpen])

    const subscriptionLoading = subscribeLoading || unsubscribeLoading

    const handleSubscribe = async (userId = null) => {
        if (!baseConversationId) return
        setSubscriptionError(null)
        try {
            await subscribeConversationMutation({
                variables: {
                    conversationId: baseConversationId,
                    userId: userId ? userId : null,
                },
            })
        } catch (error) {
            // handled in onError
        }
    }

    const handleAddSelectedSubscriber = () => {
        if (!selectedSubscriberId) return
        handleSubscribe(selectedSubscriberId)
        setSelectedSubscriberId('')
    }

    const handleSubscribeSelf = () => {
        handleSubscribe(null)
    }

    const handleUnsubscribe = async (userId) => {
        if (!baseConversationId) return
        setSubscriptionError(null)
        try {
            await unsubscribeConversationMutation({
                variables: {
                    conversationId: baseConversationId,
                    userId: userId,
                },
            })
        } catch (error) {
            // handled in onError
        }
    }

    const isCurrentUserSubscribed = currentUserIdString ? subscriberIdSet.has(currentUserIdString) : false
    const canCurrentUserSubscribe = currentUserIdString
        ? conversationMembers.some(member => member.user.id === currentUserIdString)
        : false

    if (!currentUser) {
        return <div></div>
    }

    if (conversationError) {
        return (
            <div className="CommsSession">
                <div className="alert alert-danger m-3">
                    Failed to load conversation details.
                </div>
            </div>
        )
    }

    if (!conversationId) {
        return (
            <div className="CommsSession">
                <div className="CommsSessionPlaceholder">Select a conversation to begin.</div>
            </div>
        )
    }

    if (!conversation && !conversationLoading) {
        return (
            <div className="CommsSession">
                <div className="CommsSessionPlaceholder">Conversation not found.</div>
            </div>
        )
    }
    if (!conversation && conversationLoading) {
        return <Loader />
    }
    const contactName = conversation?.contact_name ?? conversation?.contactName ?? ''
    const contactPhone = conversation?.contact_phone ?? conversation?.contactPhone ?? ''
    const conversationTitle = conversationLoading ? 'Loading conversation…' : (conversation?.subject || contactName || 'Conversation')
    const driverName = conversation?.driver?.name ?? conversation?.driver?.fullName ?? ''
    const driverId = conversation?.driver?.id ? conversation.driver.id : null
    const assignedDisplayName = conversation?.assignedTo ? formatUserDisplayName(conversation.assignedTo) : 'Unassigned'
    const hasAssigneeOption = selectedAssigneeId
        ? assignableMembers.some(member => member?.user?.id === selectedAssigneeId)
        : false
    const combinedActionError = [assignError, subscriptionError].filter(Boolean).join(' • ')
    return (
        <div>
            {!slimView && <ExternalIntegrations driverId={conversation?.driver?.id || null} />}
            <div className={`CommsSession ${slimView ? "CommsSession--slim" : ""}`}>
                <div className="CommsSessionHeader">
                    <div className="CommsHeaderLeft">
                        <RepAvatar text={contactName || conversationTitle} />&nbsp;
                        <div>
                            <div className="CommsHeaderTitle">
                                <Link to={`/comms/conversation/${conversation?.id}/`} className="CommsHeaderLink">
                                    {conversationTitle}
                                </Link>
                            </div>
                            <div className="CommsHeaderMeta">
                                {contactPhone && <span className="CommsHeaderBadge">{contactPhone}</span>}
                                {conversation?.channel?.inbox?.name && (
                                    <span className="CommsHeaderBadge CommsHeaderBadge--inbox">{conversation.channel.inbox.name}</span>
                                )}
                                {driverName && (
                                    driverId ? (
                                        <a href={`/driver/${driverId}/`} className="CommsHeaderLink">{driverName}</a>
                                    ) : (
                                        <span className="CommsHeaderBadge">{driverName}</span>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="CommsHeaderRight">
                        <div className="CommsHeaderUtilities">
                            <div className="CommsAssignmentControl">
                                <select
                                    id="conversation-assignee"
                                    className="CommsAssignmentSelect"
                                    value={selectedAssigneeId}
                                    onChange={handleAssigneeChange}
                                    disabled={assignLoading || inboxMembersLoading || assignableMembers.length === 0 || conversationLoading}
                                >
                                    <option value="">{assignableMembers.length === 0 ? 'No available agents' : 'Unassigned'}</option>
                                    {!hasAssigneeOption && selectedAssigneeId && (
                                        <option value={selectedAssigneeId}>
                                            Assigned To: {assignedDisplayName}
                                        </option>
                                    )}
                                    {assignableMembers.map(member => (
                                        <option key={member.user.id} value={member.user.id}>
                                            {selectedAssigneeId === member.user.id ? 'Assigned To: ' : ''}
                                            {formatUserDisplayName(member.user)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="CommsHeaderActions">
                            <div className="CommsSubscribersToggleWrapper">
                                <button
                                    ref={subscribersButtonRef}
                                    type="button"
                                    className="CommsHeaderMenuButton"
                                    onClick={() => {
                                        setSubscribersMenuOpen(prev => !prev)
                                        setActionMenuOpen(false)
                                    }}
                                    aria-label="Manage subscribers"
                                >
                                    <i className="fa fa-users" aria-hidden="true"></i>
                                </button>
                                {subscribersMenuOpen && (
                                    <div className="CommsSubscribersMenu" ref={subscribersMenuRef}>
                                        <div className="CommsSubscribersMenuSection">
                                            <div className="CommsSubscribersMenuLabel">Subscribers</div>
                                            <div className="CommsSubscriberList">
                                                {subscribers.length === 0 && (
                                                    <span className="CommsSubscriberPlaceholder">{conversationLoading ? 'Loading subscribers…' : 'No subscribers yet'}</span>
                                                )}
                                                {subscribers.map(subscriber => (
                                                    <div key={subscriber.id} className="CommsSubscriberChip">
                                                        <span>{formatUserDisplayName(subscriber)}</span>
                                                        <button
                                                            type="button"
                                                            className="CommsSubscriberRemove"
                                                            onClick={() => handleUnsubscribe(subscriber.id)}
                                                            disabled={subscriptionLoading}
                                                            title="Remove subscriber"
                                                        >
                                                            x
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="CommsSubscribersMenuSection">
                                            <div className="CommsSubscribersMenuLabel">Add teammate</div>
                                            <select
                                                id="conversation-subscriber"
                                                className="CommsAssignmentSelect CommsSubscriberSelect"
                                                value={selectedSubscriberId}
                                                onChange={(event) => setSelectedSubscriberId(event.target.value)}
                                                disabled={subscriptionLoading || availableSubscriberOptions.length === 0}
                                            >
                                                <option value="">{availableSubscriberOptions.length === 0 ? 'No available teammates' : 'Select teammate…'}</option>
                                                {availableSubscriberOptions.map(option => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                            <div className="CommsSubscriberButtons">
                                                <button
                                                    type="button"
                                                    className="CommsSubscriberButton"
                                                    onClick={handleAddSelectedSubscriber}
                                                    disabled={subscriptionLoading || !selectedSubscriberId}
                                                >
                                                    Add
                                                </button>
                                                <button
                                                    type="button"
                                                    className="CommsSubscriberButton secondary"
                                                    onClick={handleSubscribeSelf}
                                                    disabled={subscriptionLoading || isCurrentUserSubscribed || !canCurrentUserSubscribe}
                                                    title={!canCurrentUserSubscribe ? 'You must be part of this inbox to subscribe' : undefined}
                                                >
                                                    {isCurrentUserSubscribed ? 'Subscribed' : 'Subscribe me'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <button
                                className="CommsHeaderMenuButton"
                                onClick={() => {
                                    setSubscribersMenuOpen(false)
                                    setActionMenuOpen(!actionMenuOpen)
                                }}
                                aria-label="Conversation actions"
                                type="button"
                            >
                                <span>⋮</span>
                            </button>
                            {actionMenuOpen && (
                                <div className="CommsHeaderMenu">
                                    <ul>
                                        {isCurrentUserAssigned && (
                                            <li 
                                                onClick={() => { 
                                                    updateConversationMutation({ variables: {conversationId: conversation.id, isReadByAssignedAgent: false} });
                                                    setActionMenuOpen(false);
                                                    }} 
                                                role="button">Mark as Unread</li>
                                        )}
                                        <li onClick={() => { 
                                                updateConversationMutation({
                                                    variables: {conversationId: conversation.id, status: conversation.status === "OPEN" ? "CLOSED" : "OPEN"} 
                                                });
                                                setActionMenuOpen(false); 
                                            }} 
                                            role="button">{conversation.status === "OPEN" ? "Close Conversation" : "Reopen Conversation"}</li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {combinedActionError && (
                    <div className="alert alert-warning" style={{ margin: '12px 0' }}>
                        {combinedActionError}
                    </div>
                )}
                <div className="CommsSessionBody">
                    <div className="CommsSessionMessages" ref={messagesContainerRef}>
                        {combinedFeed.map((item, idx) => {
                            if (item.type === "note") {
                                return (
                                    <div
                                        key={`note-${item.id}`}
                                        className="CommsNote mb-2"
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "space-between",
                                            textAlign: "center",
                                            margin: "0 auto",
                                            fontStyle: "italic",
                                            color: "#666",
                                            fontSize: 13,
                                            background: "#f9f9f9",
                                            borderRadius: 8,
                                            padding: "6px 12px",
                                            width: "fit-content",
                                        }}
                                        title={moment(item.date_added).format("LLLL")}
                                    >
                                        <ReactMarkdown
                                         
                                            components={{
                                                a: ({ href, children }) => {
                                                // If href is NOT a real URL, treat as plain text
                                                const isMention = href && !href.startsWith("http");

                                                if (isMention) {
                                                    return <span className="mention">{children}</span>;
                                                }

                                                // Else: render normally or disable
                                                return <a href={href}>{children}</a>;
                                                }
                                            }}
                                        >
                                        {item.note}
                                        </ReactMarkdown>
                                        <span style={{ fontSize: 11, color: "#999", marginLeft: 8 }}>
                                            — {item.created_by_username}, {moment(item.date_added).fromNow()}
                                        </span>
                                    </div>
                                )
                            }

                            // regular message rendering
                            const attachments = normalizeMessageAttachments(item)
                            const messageBody = item?.message?.body ?? item.body ?? ''
                            const statusDescription = item?.status_description || ''
                            const messageDate = item?.scheduled_for_timestamp || item?.timestamp || null
                            const topic = item?.topic_name || null
                            return (
                                <div
                                    key={`msg-${item.id}-${idx}`}
                                    className={`CommsMessage ${item.is_inbound ? "CommsMessageUser" : "CommsMessageCustomer"}`}
                                    style={{
                                        background: '#fff',
                                        borderRadius: 12,
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                                        marginBottom: 12,
                                        padding: '12px 18px',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        position: 'relative'
                                    }}
                                >
                                    <RepAvatar text={item.is_inbound ? item.driver_name : item.created_by_username} repId={item.created_by_graphene_id} />&nbsp;
                                    <section style={{ flex: 1 }}>
                                        <header
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: '12px',
                                            }}
                                        >
                                            {/* Sender + Recipient */}
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 500, fontSize: 15 }}>{item.sender}</div>
                                                {item.recipient && (
                                                    <div style={{ fontSize: 13, color: '#888' }}>To: {item.recipient}</div>
                                                )}
                                            </div>

                                            {/* Topic */}
                                            {topic && (
                                                <span
                                                    style={{
                                                        backgroundColor: '#eef6ff',
                                                        color: '#0366d6',
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                        padding: '2px 6px',
                                                        borderRadius: 4,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {topic}
                                                </span>
                                            )}

                                            {/* Date + Status */}
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    color: '#888',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                                title={messageDate ? moment(messageDate).format('LLLL') : ''}
                                            >
                                                {messageDate ? moment(messageDate).fromNow() : null}
                                                <MessageStatus
                                                    status={item.status}
                                                    isInbound={item.is_inbound}
                                                    statusDescription={statusDescription}
                                                />
                                            </div>
                                        </header>
                                        <div>
                                            <div style={{ marginTop: 4 }}>{messageBody && (<ReactMarkdown>{messageBody.replaceAll("\n", " ")}</ReactMarkdown>)}</div>
                                            {attachments.length > 0 && (
                                                <div className="CommsMessageAttachments">
                                                    {attachments.map((attachment) => {
                                                        const sizeLabel = formatBytes(attachment.size)
                                                        return (
                                                            <a
                                                                key={attachment.id}
                                                                href={attachment.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className={`CommsMessageAttachmentCard${attachment.isImage ? ' is-image' : ''}`}
                                                            >
                                                                {attachment.isImage ? (
                                                                    <>
                                                                        <div className="CommsMessageAttachmentImage">
                                                                            <img src={attachment.url} alt={attachment.name} />
                                                                        </div>
                                                                        <div className="CommsMessageAttachmentCaption">
                                                                            <span className="CommsMessageAttachmentName">{attachment.name}</span>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <div className="CommsMessageAttachmentMeta">
                                                                        <div className="CommsMessageAttachmentIcon">
                                                                            {getAttachmentGlyph(attachment.mimeType || attachment.name)}
                                                                        </div>
                                                                        <div className="CommsMessageAttachmentText">
                                                                            <span className="CommsMessageAttachmentName">{attachment.name}</span>
                                                                            {sizeLabel && <span className="CommsMessageAttachmentSize">{sizeLabel}</span>}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </a>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                </div>
                            )
                        })}
                    </div>
                </div>
                <MessageComposer conversationId={conversationId} suggestedResponse={lastInboundSuggestedResponse} />
            </div>
        </div>
    )
}

export default withApolloClient(CommsConversation)
